import { useMemo } from 'react';
import { useSnackbar } from 'notistack';

// Redux
import { useDispatch, useSelector } from 'react-redux';

import {
  setPage,
  setTotal,
  setLimit,
  setColumnVisibility,
  setRoundTableFilters,
  setClearRoundTableFilters,
  setShowRoundStatusModalDialog,
} from '@Modal/reducers/rounds/table/roundsTableSlice';

// Graphql
import {
  useAviatorLiveRoundStatusQuery,
  useUpdateRoundStatusMutation,
} from '@Src/generated.graphql';
// Types
import { StoreInterface } from 'src/app/types';
import { FilterItemType } from '@Modal/reducers/types';

export const useRoundsTable = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [updateRoundStatus] = useUpdateRoundStatusMutation();
  const { refetch } = useAviatorLiveRoundStatusQuery();

  const {
    page,
    limit,
    total,
    selectedFilters,
    columnVisibility,
    roundTableFilters,
    columnVisibilityOptions,
    showRoundStatusModalDialog,
  } = useSelector((state: StoreInterface) => state?.roundsTable);

  const roundsListFilters = useMemo(() => {
    const columnVisibilityStrArr = Array.from(Object.values(columnVisibility)).map((filt) => filt);
    return columnVisibilityStrArr as string[];
  }, [columnVisibility]);

  const handleApplyColumnVisibility = (column: string[]) => {
    const newVisibleColumns = [...roundsListFilters, ...column];
    if (roundsListFilters.filter((col) => column.includes(col)).length > 0) {
      const filteredNewVisibleColumns = roundsListFilters.filter((col) => col !== column[0]);
      dispatch(setColumnVisibility(filteredNewVisibleColumns));
    } else {
      dispatch(setColumnVisibility(newVisibleColumns));
    }
  };

  const handleFilterSelect = (filter: FilterItemType) => {
    dispatch(setRoundTableFilters(filter));
  };

  const visiblityCustomizable = useMemo(
    () => ({
      value: columnVisibility,
      options: columnVisibilityOptions,
      apply: (value: string[]) => handleApplyColumnVisibility(value),
    }),
    [columnVisibility, columnVisibilityOptions]
  );

  const clearRoundTableFiltersHandler = () => {
    dispatch(setClearRoundTableFilters());
  };

  const handleUpdateRoundStatus = async (status: 'RUNNING' | 'PAUSED') => {
    const inputVariable = {
      configId: '662f54c5baa654b40d6f510d',
      status: status,
    };

    try {
      await updateRoundStatus({
        // @ts-ignore
        variables: { input: inputVariable },
      });

      enqueueSnackbar('Round status successfully updated', {
        variant: 'success',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });

      await refetch();

      dispatch(setShowRoundStatusModalDialog(false));
    } catch (error) {
      enqueueSnackbar(error.message || 'Failed to update round status', {
        variant: 'error',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }
  };

  return {
    // State
    limit,
    page,
    total,
    selectedFilters,
    columnVisibility,
    roundTableFilters,
    roundsListFilters,
    visiblityCustomizable,
    columnVisibilityOptions,
    showRoundStatusModalDialog,
    // Actions
    setPage,
    setLimit,
    setTotal,
    setRoundTableFilters,
    setClearRoundTableFilters,
    setShowRoundStatusModalDialog,
    // Handlers
    handleFilterSelect,
    handleUpdateRoundStatus,
    handleApplyColumnVisibility,
    clearRoundTableFiltersHandler,
  };
};
